import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import * as authService from '@/services/auth';

type OAuthProvider = 'google' | 'github';

interface AuthResult {
  error: AuthError | null;
}

export interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn(email: string, password: string): Promise<AuthResult>;
  signUp(
    email: string,
    password: string,
    fullName?: string,
  ): Promise<AuthResult>;
  signOut(): Promise<AuthResult>;
  signInWithOAuth(provider: OAuthProvider): Promise<AuthResult>;
  signInWithOtp(email: string): Promise<AuthResult>;
  resetPassword(email: string): Promise<AuthResult>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      console.info('[auth] state_change', { event });

      // On initial session, verify the user still exists server-side
      // (e.g. after a local DB reset the JWT is still cached but the user is gone)
      if (event === 'INITIAL_SESSION' && newSession?.user) {
        const { data } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', newSession.user.id)
          .maybeSingle();

        if (!data) {
          console.warn('[auth] stale session detected — signing out');
          await supabase.auth.signOut();
          return;
        }
      }

      setSession(newSession);
      setUser(newSession?.user ?? null);
      setLoading(false);

      if (event === 'SIGNED_OUT') {
        navigateRef.current('/sign-in', { replace: true });
      }

      if (event === 'PASSWORD_RECOVERY') {
        navigateRef.current('/set-new-password', { replace: true });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(
    (email: string, password: string) => authService.signIn(email, password),
    [],
  );

  const signUp = useCallback(
    (email: string, password: string, fullName?: string) =>
      authService.signUp(email, password, fullName),
    [],
  );

  const signOutFn = useCallback(() => authService.signOut(), []);

  const signInWithOAuth = useCallback(
    (provider: OAuthProvider) => authService.signInWithOAuth(provider),
    [],
  );

  const signInWithOtp = useCallback(
    (email: string) => authService.signInWithOtp(email),
    [],
  );

  const resetPasswordFn = useCallback(
    (email: string) => authService.resetPassword(email),
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      loading,
      signIn,
      signUp,
      signOut: signOutFn,
      signInWithOAuth,
      signInWithOtp,
      resetPassword: resetPasswordFn,
    }),
    [
      user,
      session,
      loading,
      signIn,
      signUp,
      signOutFn,
      signInWithOAuth,
      signInWithOtp,
      resetPasswordFn,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
